import React, { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BookingForm from "../BookingForm/BookingForm";
import { trackCallConversion, trackLeadConversion } from "../../utils/tracking";
import {
  Smartphone, ShieldCheck, ThumbsUp, Home, CheckCircle2, Clock, Wrench, Battery, Wifi, Camera,
  Volume2, Cpu, Phone, ArrowRight, Star, Check, ChevronDown, ChevronUp, X,
  Calculator, Sparkles, Award, CheckCircle, PhoneCall, Zap, RotateCcw, Droplets
} from "lucide-react";
import "./IphoneRepair.css";

const SERVICE_CATEGORIES = [
  {
    id: "screen-repair", title: "Screen & Display Repair", category: "hardware", icon: Smartphone, tag: "Most Popular",
    desc: "Expert replacement of cracked, shattered, or unresponsive iPhone screens using genuine OEM-grade OLED/LCD panels.",
    items: ["Cracked & Shattered Screen Replacement", "OLED / LCD Display Panel Swap", "Touch Screen & Digitizer Fix", "3D Touch / Haptic Feedback Restore", "True Tone & Face ID Preserved"]
  },
  {
    id: "battery-repair", title: "Battery & Charging Repair", category: "hardware", icon: Battery, tag: "Fast Fix",
    desc: "Replace degraded batteries and fix charging port issues to restore full-day battery life on any iPhone model.",
    items: ["Battery Health Diagnostic & Replacement", "Lightning / USB-C Port Deep Cleaning", "Charging Port Replacement", "Wireless Charging Module Fix", "MagSafe Coil Repair"]
  },
  {
    id: "camera-repair", title: "Camera & Lens Repair", category: "hardware", icon: Camera, tag: "Precision Work",
    desc: "Restore blurry, cracked, or malfunctioning iPhone cameras — front TrueDepth, rear main, ultrawide & telephoto.",
    items: ["Rear Main & Ultrawide Camera Replacement", "Front TrueDepth / Face ID Camera Fix", "Camera Glass Lens Cover Replacement", "OIS (Optical Image Stabilization) Fix", "Telephoto & Macro Lens Module"]
  },
  {
    id: "water-damage", title: "Water Damage & Corrosion", category: "advanced", icon: Droplets, tag: "Expert Only",
    desc: "Advanced board-level ultrasonics cleaning, corrosion removal, and microsoldering for water-damaged iPhones.",
    items: ["Ultrasonic Board Cleaning", "Corrosion & Oxidation Removal", "Component-Level Microsoldering", "Liquid Damage Diagnostic", "Data Recovery from Damaged Board"]
  },
  {
    id: "speaker-audio", title: "Speaker, Mic & Audio", category: "hardware", icon: Volume2, tag: "Quick Repair",
    desc: "Fix muffled, silent, or distorted audio from earpiece, loudspeaker, and microphone assemblies.",
    items: ["Loudspeaker & Earpiece Replacement", "Microphone Array Replacement", "No Sound / Distorted Audio Fix", "Ringer Switch Repair", "Bluetooth Audio Chip Fix"]
  },
  {
    id: "motherboard-repair", title: "Motherboard & IC Repair", category: "advanced", icon: Cpu, tag: "Microsoldering",
    desc: "Professional board-level repairs: NAND, PMIC, Touch IC, Face ID chip, and dead iPhone recovery.",
    items: ["No Power / Dead iPhone Recovery", "Touch IC & Face ID Chip Reballing", "PMIC & Baseband IC Repair", "NAND Flash Chip Replacement", "Short Circuit Diagnosis & Fix"]
  },
  {
    id: "software-unlock", title: "Software & iCloud Issues", category: "software", icon: Wifi, tag: "Remote Fix",
    desc: "Resolve iOS crashes, bootloops, iCloud activation locks, backup & restore issues without data loss.",
    items: ["iOS Bootloop & Crash Recovery", "Factory Reset Without Data Loss", "iTunes / Finder DFU Mode Restore", "iCloud Backup & Migration Help", "App Crash & Freeze Diagnostics"]
  },
  {
    id: "back-glass", title: "Back Glass & Frame Repair", category: "hardware", icon: RotateCcw, tag: "Cosmetic Fix",
    desc: "Replace shattered back glass panels and bent aluminium/titanium frames across iPhone 8 to iPhone 16 Pro series.",
    items: ["Back Glass Panel Replacement (iPhone 8-16)", "Aluminium & Titanium Frame Straightening", "Side Button & Power Button Repair", "Volume Button & Mute Switch Fix", "Full Housing / Chassis Replacement"]
  }
];

const ESTIMATOR_DATA = {
  hardware: {
    label: "Hardware Repair",
    options: [
      { name: "Screen Replacement (iPhone 13/14 Series)", unitPrice: 3500, unit: "repair", estTime: "45-60 mins" },
      { name: "Screen Replacement (iPhone 15/15 Pro Series)", unitPrice: 5500, unit: "repair", estTime: "60-90 mins" },
      { name: "Battery Replacement (Any Model)", unitPrice: 1200, unit: "repair", estTime: "30 mins" },
      { name: "Charging Port Replacement", unitPrice: 1500, unit: "repair", estTime: "45 mins" },
      { name: "Rear Camera Module Replacement", unitPrice: 3800, unit: "repair", estTime: "60 mins" },
      { name: "Speaker / Earpiece Replacement", unitPrice: 900, unit: "repair", estTime: "30 mins" },
      { name: "Back Glass Replacement (iPhone 12-16)", unitPrice: 2200, unit: "repair", estTime: "60 mins" }
    ]
  },
  advanced: {
    label: "Advanced / Board-Level Repair",
    options: [
      { name: "Water Damage Recovery & Cleaning", unitPrice: 3500, unit: "repair", estTime: "2-4 Hours" },
      { name: "Motherboard Microsoldering (IC Reballing)", unitPrice: 5500, unit: "repair", estTime: "1-2 Days" },
      { name: "Dead iPhone No-Power Recovery", unitPrice: 4500, unit: "repair", estTime: "2-6 Hours" },
      { name: "NAND / PMIC Chip Replacement", unitPrice: 7500, unit: "repair", estTime: "1-3 Days" },
      { name: "Face ID TrueDepth Dot Projector Fix", unitPrice: 4000, unit: "repair", estTime: "2-3 Hours" }
    ]
  }
};

const WORKFLOW_STEPS = [
  { step: "01", title: "Free Doorstep Diagnostic", desc: "Our certified technician arrives at your home or office, runs a full hardware & software diagnostic, and provides an upfront fixed quote with no hidden fees." },
  { step: "02", title: "Genuine OEM-Grade Parts Only", desc: "We source premium OEM-equivalent components — Apple-matched OLED panels, grade-A batteries with certified cell health, and precision optical camera modules." },
  { step: "03", title: "Expert Repair in 30-90 Mins", desc: "Most screen and battery repairs are completed on-site in under 90 minutes. Advanced microsoldering jobs are handled at our workshop with same/next-day turnaround." },
  { step: "04", title: "QC Test, Warranty & Handover", desc: "Every repair passes a 20-point quality check: screen response, Face ID, cameras, speaker, charging, and water resistance. Backed by a 90-Day Repair Warranty." }
];

const SUPPORTED_MODELS = [
  "iPhone 16 Pro Max", "iPhone 16 Pro", "iPhone 16 Plus", "iPhone 16",
  "iPhone 15 Pro Max", "iPhone 15 Pro", "iPhone 15 Plus", "iPhone 15",
  "iPhone 14 Pro Max", "iPhone 14 Pro", "iPhone 14 Plus", "iPhone 14",
  "iPhone 13 Pro Max", "iPhone 13 Pro", "iPhone 13 Mini", "iPhone 13",
  "iPhone 12 Pro Max", "iPhone 12 Pro", "iPhone 12 Mini", "iPhone 12",
  "iPhone 11 Pro Max", "iPhone 11 Pro", "iPhone 11",
  "iPhone XS Max", "iPhone XS", "iPhone XR", "iPhone X",
  "iPhone SE (2nd & 3rd Gen)", "iPhone 8 Plus", "iPhone 8"
];

const TESTIMONIALS = [
  { name: "Priya Mehta", role: "iPhone 15 Pro — Screen Replacement", rating: 5, comment: "My iPhone 15 Pro had a completely shattered screen after a fall. DSMR's technician arrived within 45 minutes, replaced the OLED panel at home in under an hour. Face ID, True Tone, and ProMotion all work perfectly. Excellent service!" },
  { name: "Arjun Kapoor", role: "iPhone 14 — Water Damage Recovery", rating: 5, comment: "Dropped my iPhone 14 in water and it stopped turning on. The DSMR team did an ultrasonic board clean and microsoldering job. Got my phone back working in less than 6 hours — and all my data was safe. Absolute lifesavers!" },
  { name: "Simran Ahuja", role: "iPhone 13 — Battery & Charging Port", rating: 5, comment: "Battery was draining in 3 hours and the charging port was loose. DSMR replaced both at my office doorstep for a transparent price. No surprise fees, 90-day warranty, super professional team. Highly recommend!" }
];

const FAQ_ITEMS = [
  { q: "Do you repair all iPhone models including the latest iPhone 16 series?", a: "Yes! We repair all iPhone models from iPhone 8 through iPhone 16 Pro Max, including Mini, Plus, and SE variants. Our technicians are trained on the latest Apple hardware and have specialised tools to handle them." },
  { q: "Will my Face ID stop working after a screen replacement?", a: "No. Our certified technicians use the Apple-proprietary screen serialization process and genuine OEM-grade OLED panels to preserve Face ID, True Tone, and ProMotion calibration after every screen repair. We never break biometric security." },
  { q: "How fast can a technician arrive for an iPhone repair?", a: "Our doorstep technicians are stationed across Delhi NCR and typically arrive within 30 to 60 minutes. Common repairs like battery, screen, and charging port take only 30-90 minutes on-site." },
  { q: "Do you use genuine Apple parts or third-party components?", a: "We use OEM-equivalent Grade-A components matched to Apple specifications. For screen replacements, we use OLED panels manufactured on the same assembly lines as Apple OEM panels, ensuring full display calibration and brightness." },
  { q: "What warranty do you offer on iPhone repairs?", a: "All iPhone repairs carry a 90-Day Parts & Labour Warranty. If the repaired component fails within 90 days due to workmanship or part defect, we fix it at zero cost. Water damage and microsoldering jobs carry a 30-Day Warranty." },
  { q: "Can you recover data from a dead / water-damaged iPhone?", a: "Yes. Our advanced microsoldering team specializes in board-level data recovery from water-damaged, dead, and bootloop-stuck iPhones. Recovery success rate exceeds 85% even for severely damaged boards." }
];


const COMMON_ISSUES = [
  { title: "Display & Screen Repair", image: "/broken_display.webp" },
  { title: "Battery Health & Drain", image: "/images/iphone_battery_problem.png" },
  { title: "Camera & Lens Repair", image: "/camera_issue.webp" },
  { title: "Charging Port Fix", image: "/charging_issue.webp" },
  { title: "Back Glass Replacement", image: "/images/services/iphone.jpg" },
  { title: "Water Damage Recovery", image: "/water_dam.webp" },
  { title: "Speaker & Audio Fix", image: "/speaker_issue.webp" },
  { title: "Face ID Sensor Repair", image: "/images/about_repair.png" }
];

const staggerContainerViewport = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeInUpViewport = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const fadeInLeftViewport = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const fadeInRightViewport = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

function CommoniPhoneProblemsSection({ brand = 'apple', model = 'iphone', location = 'delhi', issues = COMMON_ISSUES }) {
  const normalize = (text) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const problemTypeFromTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes('back glass')) return 'back-glass';
    if (t.includes('battery')) return 'battery';
    if (t.includes('camera')) return 'camera';
    if (t.includes('charging')) return 'chargin-port';
    if (t.includes('display')) return 'display';
    if (t.includes('microphone')) return 'microphone';
    if (t.includes('speaker')) return 'speaker';
    if (t.includes('Display')) return 'Display';
    if (t.includes('face id')) return 'face-id';
    if (t.includes('water')) return 'water-damage';
    return normalize(title);
  };

  const buildUrl = (title) => {
    const pt = problemTypeFromTitle(title);
    const b = normalize(brand);
    const m = normalize(model);
    const loc = normalize(location);
    return `/${b}/${m}/${pt}/${b}-${m}-${pt}-fix-in-${loc}`;
  };

  return (
    <motion.section
      className="common-iphone-problems-section"
      variants={staggerContainerViewport}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2 }}
    >
      <div className="iphone-container flex-col-center">
        <motion.div variants={fadeInUpViewport} className="iphone-section-header center">
          <span className="iphone-section-tag">QUICK DIAGNOSTICS</span>
          <h2 className="iphone-section-title">Common iPhone Problems</h2>
          <p className="iphone-section-desc">Select your issue to explore repair solutions, estimated costs, and instant doorstep technician availability.</p>
        </motion.div>
        <div className="common-problems-grid">
          {issues.map((issue, idx) => (
            <motion.div
              key={issue.title}
              variants={idx % 2 === 0 ? fadeInLeftViewport : fadeInRightViewport}
            >
              <Link
                to={buildUrl(issue.title)}
                className="problem-card-link"
              >
                <div className="problem-img-wrapper">
                  <img
                    src={issue.image}
                    alt={issue.title}
                    width={480}
                    height={200}
                    className="problem-card-img"
                    loading="lazy"
                  />
                </div>
                <h3 className="problem-card-title">{issue.title}</h3>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}


function SliderShowcase() {
  const showcaseRef = useRef(null);

  const scrollByAmount = (amount) => {
    const el = showcaseRef.current;
    if (!el) return;

    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const items = [
    { key: 'ip17', label: 'iPhone 17', img: '/mobiles/iphone/iphone17/iphone_17.webp', alt: 'iPhone 17 fix service in Delhi' },
    { key: 'ip16', label: 'iPhone 16', img: '/mobiles/iphone/iphone16/iphone_16.webp', alt: 'iPhone 16 fix service in Delhi' },
    { key: 'ip15', label: 'iPhone 15', img: '/mobiles/iphone/iphone15/iphone_15.webp', alt: 'iPhone 15 series fix service in Delhi' },
    { key: 'ip14', label: 'iPhone 14', img: '/mobiles/iphone/iphone14/iphone_14.webp', alt: 'iPhone 14 fix service in Delhi' },
    { key: 'ip13', label: 'iPhone 13', img: '/mobiles/iphone/iphone13/iphone_13.webp', alt: 'iPhone 13 fix service in Delhi' },
    { key: 'ip12', label: 'iPhone 12', img: '/mobiles/iphone/iphone12/iphone_12.webp', alt: 'iPhone 12 fix service in Delhi' },
    { key: 'ip11', label: 'iPhone 11', img: '/mobiles/iphone/iphone11/iphone_11.webp', alt: 'iPhone 11 fix service in Delhi' },
    { key: 'ipx', label: 'iPhone X', img: '/mobiles/iphone/iphonex/iphone_x.webp', alt: 'iPhone X fix service in Delhi' },
    { key: 'ip8', label: 'iPhone 8', img: '/mobiles/iphone/old_iphone.webp', alt: 'iPhone 8 fix service in Delhi' },
    { key: 'ip7', label: 'iPhone 7', img: '/mobiles/iphone/old_iphone.webp', alt: 'iPhone 7 fix service in Delhi' },
    { key: 'ip6', label: 'iPhone 6', img: '/mobiles/iphone/old_iphone.webp', big: true, alt: 'iPhone 6 fix service in Delhi' },
    { key: 'pro', label: 'Pro versions', img: '/mobiles/iphone/iphone15/iphone_15.webp', alt: 'iPhone Pro fix service in Delhi' },
  ];

  return (
    <div className="showcaseWrap">
      <div className="showcase-nav-header">
        <span className="showcase-hint">Scroll to explore variants</span>
        <div className="showcase-controls">
          <button
            type="button"
            className="showcase-nav-btn"
            onClick={() => scrollByAmount(-280)}
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            className="showcase-nav-btn"
            onClick={() => scrollByAmount(280)}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      </div>
      <div ref={showcaseRef} className="iphoneShowcase">
        {items.map((item) => {
          const slug = (item.label || item.key)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          const href = `#estimator`;

          return (
            <div key={item.key} className="iphoneModel">
              <a href={href} aria-label={`${item.label} fix in Delhi`} className="model-slider-card">
                <div className={`deviceBox ${item.big ? 'big' : ''}`}>
                  <img
                    src={item.img}
                    alt={item.alt || item.label}
                    width={75}
                    height={100}
                    className="deviceImg"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/services/iphone.png";
                    }}
                  />
                </div>
                <span className="model-slider-label">{item.label}</span>
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function IphoneRepair() {
  const [activeTab, setActiveTab] = useState("all");
  const [calcType, setCalcType] = useState("hardware");
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openFaq, setOpenFaq] = useState(0);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingService, setBookingService] = useState("iPhone Repair");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", model: "iPhone 15 Pro", issue: "", address: "", date: "", notes: "" });

  const filteredServices = useMemo(() => {
    if (activeTab === "all") return SERVICE_CATEGORIES;
    return SERVICE_CATEGORIES.filter(s => s.category === activeTab);
  }, [activeTab]);

  const currentCalcOptions = ESTIMATOR_DATA[calcType].options;
  const currentCalcItem = currentCalcOptions[selectedOptionIdx] || currentCalcOptions[0];
  const totalPrice = currentCalcItem.unitPrice * quantity;

  const handleOpenBooking = (serviceName = "iPhone Repair") => {
    setBookingService(serviceName);
    setFormData(prev => ({ ...prev, issue: serviceName }));
    setIsBookingOpen(true);
    setFormSubmitted(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      mobileNumber: formData.phone || '',
      customerName: formData.name || 'Not provided',
      email: formData.email ? formData.email.trim() : null,
      address: formData.address || 'Not provided',
      deviceType: 'iPhone',
      brandModel: `iPhone ${formData.model || ''}`.trim(),
      issueDescription: `${formData.issue || bookingService || 'iPhone Repair'}. Notes: ${formData.notes || 'None'}`,
    };

    try {
      await fetch('https://dsmr-api.vercel.app/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.warn('Vercel API error (iPhone):', err);
    } finally {
      setFormSubmitted(true);
      trackLeadConversion({
        mobileNumber: formData.phone,
        email: formData.email,
        device: 'iPhone',
        model: formData.model,
      });
      setTimeout(() => { setIsBookingOpen(false); setFormSubmitted(false); }, 4000);
    }
  };

  return (
    <div className="iphone-page">
      {/* HERO */}
      <section className="iphone-hero">
        <div className="iphone-hero-glow"></div>
        <div className="iphone-container hero-content-wrap">
          <div className="hero-text-col">
            <div className="iphone-hero-badge">
              <Sparkles size={14} className="badge-sparkle" />
              <span>CERTIFIED iPHONE REPAIR SPECIALISTS - DOORSTEP SERVICE</span>
            </div>
            <h1 className="iphone-hero-title">
              Expert <span className="iphone-highlight">iPhone Repair</span> at Your Doorstep
            </h1>
            <p className="iphone-hero-subtitle">
              Cracked screen, dead battery, water damage, or motherboard issues — our Apple-trained technicians
              fix any iPhone model at your home or office across Delhi NCR. Genuine OEM parts. 90-Day warranty.
            </p>
            <div className="iphone-perks-grid">
              <div className="iphone-perk-pill"><Clock size={15} /><span>30-60 Min Arrival</span></div>
              <div className="iphone-perk-pill"><ShieldCheck size={15} /><span>1-year Warranty</span></div>
              <div className="iphone-perk-pill"><CheckCircle2 size={15} /><span>OEM-Grade Parts</span></div>

            </div>
            <div className="iphone-hero-actions">
              <button className="iphone-btn primary-iphone-btn" onClick={() => handleOpenBooking("Doorstep iPhone Repair")}>
                <Smartphone size={18} /><span>Book iPhone Repair</span>
              </button>
              <a href="#estimator" className="iphone-btn secondary-iphone-btn">
                <Calculator size={18} /><span>Get Cost Estimate</span>
              </a>
              <a
                href="tel:+918700319769"
                className="iphone-btn emergency-iphone-btn"
                data-dsmr-phone="true"
                onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
              >
                <PhoneCall size={18} /><span>Call Now</span>
              </a>
            </div>
          </div>
          <div className="hero-image-col">
            <div className="iphone-hero-img-card">
              <img src="/images/iphone_repair_hero.png" alt="Professional iPhone Repair Technician" className="iphone-hero-img" />
              <div className="hero-float-badge badge-tr">
                <div className="float-icon-box"><Smartphone size={20} /></div>
                <div><span className="float-title">5,000+ iPhones Fixed</span><span className="float-sub">All Models - All Issues</span></div>
              </div>
              <div className="hero-float-badge badge-bl">
                <div className="float-icon-box blue"><ShieldCheck size={20} /></div>
                <div><span className="float-title">90-Day Warranty</span><span className="float-sub">On Every Repair</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      {/* <section className="iphone-trust-bar">
        <div className="iphone-container trust-inner">
          <p className="trust-label">TRUSTED BY 5,000+ iPHONE USERS ACROSS DELHI NCR</p>
          <div className="trust-pills-row">
            <div className="trust-pill"><CheckCircle2 size={14} /><span>Apple-Trained Technicians</span></div>
            <div className="trust-pill"><ShieldCheck size={14} /><span>OEM-Grade Components</span></div>
            <div className="trust-pill"><Clock size={14} /><span>Same-Day Most Repairs</span></div>
            <div className="trust-pill"><Award size={14} /><span>4.9 Star - 2,400+ Reviews</span></div>
            <div className="trust-pill"><Smartphone size={14} /><span>iPhone 8 to 16 Pro Max</span></div>
          </div>
        </div>
      </section> */}

      {/* STATS BAR */}
      {/* <section className="iphone-stats-bar">
        <div className="iphone-container iphone-stats-grid">
          <div className="iphone-stat-card"><span className="iphone-stat-num">5,000+</span><span className="iphone-stat-label">iPhones Repaired</span></div>
          <div className="iphone-stat-card"><span className="iphone-stat-num">30 Min</span><span className="iphone-stat-label">Average Arrival Time</span></div>
          <div className="iphone-stat-card"><span className="iphone-stat-num">98.7%</span><span className="iphone-stat-label">First-Fix Success Rate</span></div>
          <div className="iphone-stat-card"><span className="iphone-stat-num">4.9 Star</span><span className="iphone-stat-label">Rating - 2,400+ Reviews</span></div>
        </div>
      </section> */}

      <section className="iphoneModelsSection">
        <div className="iphone-container modelsContent">
          <div className="modelsLeft">
            <div className="iphone-section-header center">
              <span className="iphone-section-tag"></span>
              <h2 className="iphone-section-title">All iPhone Models </h2>
              <p className="iphone-section-desc">
                Professional fix services for 25+ iPhone variants including all series from iPhone 6 to iPhone 17 Pro Max.
              </p>
            </div>
            <SliderShowcase />
          </div>
        </div>
      </section>

      {/* COMMON iPHONE PROBLEMS */}
      <div id="services">
        <CommoniPhoneProblemsSection />
      </div>

      {/* COST ESTIMATOR */}
      <section className="iphone-estimator-section" id="estimator">
        <div className="iphone-container">
          <div className="iphone-estimator-wrap">
            <div className="estimator-info-col">
              <span className="iphone-section-tag">TRANSPARENT PRICING</span>
              <h2 className="iphone-section-title">iPhone Repair Cost Estimator</h2>
              <p className="iphone-section-desc">Get an instant price estimate before booking. No hidden charges, no surprise fees at the doorstep.</p>
              <div className="estimator-type-btns">
                <button className={`est-type-btn ${calcType === "hardware" ? "active" : ""}`} onClick={() => { setCalcType("hardware"); setSelectedOptionIdx(0); setQuantity(1); }}>
                  <Wrench size={15} /><span>Hardware </span>
                </button>
                <button className={`est-type-btn ${calcType === "advanced" ? "active" : ""}`} onClick={() => { setCalcType("advanced"); setSelectedOptionIdx(0); setQuantity(1); }}>
                  <Cpu size={15} /><span>Advanced / Board-Level</span>
                </button>
              </div>
              <div className="estimator-guarantees">
                <div className="est-guarantee-row"><ShieldCheck size={17} /><span>Fixed upfront pricing - no hidden labor fees</span></div>
                <div className="est-guarantee-row"><CheckCircle2 size={17} /><span>OEM-grade genuine quality components</span></div>
                <div className="est-guarantee-row"><Award size={17} /><span>90-Day repair warranty on all hardware jobs</span></div>
              </div>
            </div>
            <div className="estimator-calc-col">
              <div className="iphone-calc-card">
                <div className="calc-card-header">
                  <h3>Select Repair and iPhone Model</h3>
                  <span className="calc-badge-tag">{ESTIMATOR_DATA[calcType].label}</span>
                </div>
                <div className="calc-form-group">
                  <label>Select Repair Type:</label>
                  <select value={selectedOptionIdx} onChange={(e) => setSelectedOptionIdx(Number(e.target.value))} className="calc-dropdown">
                    {currentCalcOptions.map((opt, i) => (
                      <option key={i} value={i}>{opt.name} - Rs.{opt.unitPrice.toLocaleString("en-IN")}</option>
                    ))}
                  </select>
                </div>
                <div className="calc-form-group">
                  <label>Number of Devices:</label>
                  <div className="qty-controls">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="qty-btn">-</button>
                    <span className="qty-display">{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)} className="qty-btn">+</button>
                  </div>
                </div>
                <div className="calc-result-box">
                  <div className="result-main-row">
                    <span>Starting From:</span>
                    <span className="result-price">Rs.{totalPrice.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="result-details-row">
                    <span>Est. Time: <strong>{currentCalcItem.estTime}</strong></span>
                    <span>Warranty: <strong>{calcType === "advanced" ? "6-Months" : "12-Months"}</strong></span>
                  </div>
                </div>
                <button className="calc-book-btn" onClick={() => handleOpenBooking(`${currentCalcItem.name} (x${quantity}) - Rs.${totalPrice.toLocaleString("en-IN")}`)}>
                  <span>Book This Repair</span><ArrowRight size={17} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IPHONE MODELS SLIDER SHOWCASE SECTION */}


      {/* SUPPORTED MODELS CHIPS */}
      {/* <section className="iphone-models-section">
        <div className="iphone-container">
          <div className="iphone-section-header center">
            <span className="iphone-section-tag">DEVICE COMPATIBILITY</span>
            <h2 className="iphone-section-title">All Supported Models List</h2>
            <p className="iphone-section-desc">From the latest iPhone 16 Pro Max to older iPhone 8, we carry parts for every model.</p>
          </div>
          <div className="iphone-models-grid">
            {SUPPORTED_MODELS.map((model, idx) => (
              <div className="model-chip" key={idx}><Smartphone size={13} /><span>{model}</span></div>
            ))}
          </div>
          <div className="models-cta-row">
            <p>Do not see your model? <strong>Call us</strong> - we likely have the part!</p>
            <a href="tel:+918700319769" className="models-call-btn"><Phone size={16} /><span>+91 8700319769</span></a>
          </div>
        </div>
      </section> */}

      {/* WHY CHOOSE US */}
      <section className="iphone-why-section">
        <div className="iphone-container">
          <div className="iphone-section-header center">
            <span className="iphone-section-tag">OUR ADVANTAGE</span>
            <h2 className="iphone-section-title">Why Choose DSMR for iPhone Repair?</h2>
            <p className="iphone-section-desc">We are Apple-focused specialists providing fast doorstep repair, guaranteed warranty, and transparent pricing.</p>
          </div>
          <div className="iphone-why-grid-v2">
            {[
              { icon: Clock, title: "Fast Turnaround", desc: "Most services completed within 30 minutes", highlight: "30 Minutes" },
              { icon: ShieldCheck, title: "Warranty Protected", desc: "Up to 6-Months warranty on all services", highlight: "6 Months" },
              { icon: Home, title: "Doorstep Service", desc: "We come to your home or office location", highlight: "At Your Location" },
              { icon: Award, title: "Quality Guaranteed", desc: "Premium parts and meticulous workmanship", highlight: "100%" },
              { icon: Zap, title: "Instant Booking", desc: "Book online in under 2 minutes", highlight: "2 Min" },
              { icon: ThumbsUp, title: "Customer Satisfaction", desc: "Rated 4.9/5 by our customers", highlight: "4.9/5" }
            ].map(({ icon: Icon, title, desc, highlight }, idx) => (
              <div className="iphone-why-card-v2" key={idx}>
                <div className="why-icon-box-v2">
                  <Icon size={20} />
                </div>
                <div className="why-card-content-v2">
                  <h3 className="why-card-title-v2">{title}</h3>
                  <p className="why-card-desc-v2">{desc}</p>
                  <div className="why-card-stat-v2">{highlight}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* REPAIR PROCESS */}
      {/* 
       */}

      {/* TESTIMONIALS */}
      <section className="iphone-testimonials-section">
        <div className="iphone-container">
          <div className="iphone-section-header center">
            <span className="iphone-section-tag">CUSTOMER REVIEWS</span>
            <h2 className="iphone-section-title">What Our iPhone Customers Say</h2>
          </div>
          <div className="iphone-testi-grid">
            {TESTIMONIALS.map((t, idx) => (
              <div className="iphone-testi-card" key={idx}>
                <div className="testi-stars-row">{[...Array(t.rating)].map((_, i) => <Star key={i} size={15} fill="#F5B93A" color="#F5B93A" />)}</div>
                <p className="testi-quote">"{t.comment}"</p>
                <div className="testi-user-row">
                  <div className="testi-avatar">{t.name.charAt(0)}</div>
                  <div><h4 className="testi-name">{t.name}</h4><span className="testi-role">{t.role}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING / CONTACT FORM */}
      <BookingForm />

      {/* FAQ */}
      {/* <section className="iphone-faq-section">
        <div className="iphone-container">
          <div className="iphone-section-header center">
            <span className="iphone-section-tag">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="iphone-section-title">iPhone Repair FAQs</h2>
          </div>
          <div className="iphone-faq-list">
            {FAQ_ITEMS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div className={`iphone-faq-item ${isOpen ? "open" : ""}`} key={idx}>
                  <button className="iphone-faq-trigger" onClick={() => setOpenFaq(isOpen ? -1 : idx)}>
                    <span>{faq.q}</span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {isOpen && <div className="iphone-faq-content"><p>{faq.a}</p></div>}
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* CTA BANNER */}
      {/* <section className="iphone-cta-banner">
        <div className="iphone-container iphone-cta-inner">
          <div className="cta-text-block">
            <h2>iPhone Broken? Get It Fixed Today at Your Door.</h2>
            <p>Our certified technicians are available across Delhi NCR. 30-60 min arrival. Upfront fixed pricing.</p>
          </div>
          <div className="cta-btn-group">
            <a href="tel:+918700319769" className="cta-call-iphone-btn"><Phone size={18} /><span>Call +91 8700319769</span></a>
            <button className="cta-book-iphone-btn" onClick={() => handleOpenBooking("Emergency iPhone Repair")}><Smartphone size={18} /><span>Book Repair Now</span></button>
          </div>
        </div>
      </section> */}

      {/* LOCAL SEO COVERAGE SECTION */}
      <section className="local-seo-section">
        <div className="iphone-container">
          <div className="iphone-section-header center">
            <span className="iphone-section-tag">COMPLETE COVERAGE</span>
            <h2 className="iphone-section-title">Services Across Delhi NCR - Complete Coverage</h2>
            <p className="iphone-section-desc">
              Professional doorstep services in all major areas of Delhi, Gurgaon, Noida, and Faridabad
            </p>
          </div>

          <div className="seo-areas-grid">
            <div className="seo-area-card">
              <div className="seo-area-icon">🏙️</div>
              <h3 className="seo-area-title">Our South Delhi Service Areas</h3>
              <div className="seo-area-list-stacked">
                {[
                  'Vasant Kunj',
                  'Shanti Niketan',
                  'Defence Colony',
                  'Greater Kailash (GK) I & II',
                  'Jor Bagh',
                  'Hauz Khas Enclave',
                  'Panchsheel Park',
                ].map((t) => (
                  <div key={t} className="seo-area-row">
                    <span className="seo-check-mark">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="seo-area-card">
              <div className="seo-area-icon">🏘️</div>
              <h3 className="seo-area-title">Our West Delhi Service Areas</h3>
              <div className="seo-area-list-stacked">
                {[
                  'Punjabi Bagh',
                  'Rajouri Garden',
                  'Patel Nagar (East & West)',
                  'Janakpuri (A,B,C Blocks)',
                  'Vikaspuri',
                  'Paschim Vihar',
                  'Kirti Nagar',
                ].map((t) => (
                  <div key={t} className="seo-area-row">
                    <span className="seo-check-mark">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="seo-area-card">
              <div className="seo-area-icon">🌆</div>
              <h3 className="seo-area-title">Our NCR Service Areas</h3>
              <div className="seo-area-list-stacked">
                {[
                  'Gurgaon',
                  'Noida',
                  'Faridabad',
                  'Greater Noida',
                  'Golf Course Road',
                  'DLF City',
                  'Sushant Lok 1',
                ].map((t) => (
                  <div key={t} className="seo-area-row">
                    <span className="seo-check-mark">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* BOOKING MODAL */}
      {isBookingOpen && (
        <div className="iphone-modal-overlay">
          <div className="iphone-modal-card">
            <button className="iphone-modal-close" onClick={() => setIsBookingOpen(false)}><X size={20} /></button>
            {!formSubmitted ? (
              <>
                <div className="iphone-modal-header">
                  <div className="modal-icon-ring"><Smartphone size={22} /></div>
                  <div><h3>Book iPhone Repair</h3><p className="modal-sub-text">Service: <strong>{bookingService}</strong></p></div>
                </div>
                <form className="iphone-booking-form" onSubmit={handleFormSubmit}>
                  <div className="iphone-form-group">
                    <label>Full Name *</label>
                    <input type="text" required placeholder="e.g. Priya Mehta" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                  </div>
                  <div className="iphone-form-group">
                    <label>Phone Number *</label>
                    <input type="tel" required placeholder="e.g. 9876543210" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="iphone-form-group">
                    <label>iPhone Model</label>
                    <select value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })}>
                      {SUPPORTED_MODELS.map((m, i) => <option key={i} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="iphone-form-group">
                    <label>Issue / Service Required</label>
                    <select value={formData.issue} onChange={(e) => setFormData({ ...formData, issue: e.target.value })}>
                      <option>Screen Replacement</option>
                      <option>Battery Replacement</option>
                      <option>Charging Port Repair</option>
                      <option>Camera Repair</option>
                      <option>Water Damage Recovery</option>
                      <option>Speaker / Microphone Fix</option>
                      <option>Motherboard Repair</option>
                      <option>Back Glass Replacement</option>
                      <option>Software / iOS Issue</option>
                      <option>Other Issue</option>
                    </select>
                  </div>
                  <div className="iphone-form-group">
                    <label>Your Address *</label>
                    <input type="text" required placeholder="e.g. Sector 49, Gurgaon" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                  </div>
                  <div className="iphone-form-group">
                    <label>Preferred Date</label>
                    <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                  </div>
                  <div className="iphone-form-group">
                    <label>Additional Notes</label>
                    <textarea rows="3" placeholder="Describe the problem or any specific issue you are facing..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                  </div>
                  <button type="submit" className="iphone-submit-btn"><CheckCircle size={18} /><span>Confirm Booking</span></button>
                </form>
              </>
            ) : (
              <div className="iphone-success-state">
                <div className="success-ring"><CheckCircle2 size={46} /></div>
                <h3>Repair Booked!</h3>
                <p>Thank you, <strong>{formData.name}</strong>! Our iPhone repair technician has been assigned. We will call you at <strong>{formData.phone}</strong> within 10 minutes to confirm arrival.</p>
                <div className="success-dispatch-tag">Dispatching Technician</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
